import React from 'react';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { Banner } from 'components/banner';
import { DISMISSAL_DURATIONS } from 'components/banner/constants/dismissalDurations';
import { trackEvent } from 'utils/google-tag-manager';
import { isTenantExpUS } from '@tenants/utils/which-tenant';
import { TenantId } from '@tenants/types';


export interface PageBannerConfig {
  title?: string | React.ReactNode;
  message?: string;
  buttonText?: string;
  isVisible?: boolean;
  showIcon?: boolean;
  showCloseButton?: boolean;
  severity?: 'error' | 'warning' | 'info' | 'success';
  icon?: React.ReactNode;
  gtmEventName?: string;
  gtmEventCategory?: string;
  gtmEventLabel?: string;
  gtmButtonClickEventName?: string;
  gtmCloseEventName?: string;
  surveyUrl?: string;
}

interface PageBannerProps {
  tenantId: TenantId;
  featureFlag: string;
  defaultConfig: PageBannerConfig;
  storageKey: string;
  storageStrategy?: 'cookie' | 'localStorage' | 'sessionStorage' | 'memory';
  dismissalDuration?: number;
  onDismiss?: () => void;
  testId: string;
}

const PageBanner: React.FC<PageBannerProps> = ({
  tenantId,
  featureFlag,
  defaultConfig,
  storageKey,
  storageStrategy = 'cookie',
  dismissalDuration = DISMISSAL_DURATIONS.ONE_DAY,
  onDismiss,
  testId,
}) => {
  const growthbook = useGrowthBook();
  
  const growthbookConfig = growthbook.getFeatureValue<PageBannerConfig>(
    featureFlag,
    defaultConfig
  );

  // Merge GrowthBook config with defaults to ensure all properties are preserved
  const config = {
    ...defaultConfig,
    ...growthbookConfig,
  };

  // Special handling for feedback banner: combine icon with title from GrowthBook
  if (featureFlag.includes('feedback') && config.title && typeof config.title === 'string' && defaultConfig.icon) {
    config.title = (
      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {defaultConfig.icon}
        </span>
        {config.title}
      </span>
    );
    config.showIcon = false; // Hide the separate icon since it's now in the title
  }

  if (!config?.isVisible || !config.message) {
    return null;
  }

  // For feedback banner, only show for expUS tenant
  if (featureFlag.includes('feedback') && !isTenantExpUS(tenantId)) {
    return null;
  }

  const handleButtonClick = () => {
    // Track GTM event for button click (survey click)
    if (config.gtmButtonClickEventName) {
      trackEvent(config.gtmButtonClickEventName);
    }
    
    // Open survey URL in new tab if provided
    if (config.surveyUrl) {
      window.open(config.surveyUrl, '_blank', 'noopener,noreferrer');
    }
    
    onDismiss?.();
  };

  const handleCloseClick = () => {
    // Track GTM event for close button click
    if (config.gtmCloseEventName) {
      trackEvent(config.gtmCloseEventName);
    }
    
    onDismiss?.();
  };

  return (
    <Banner
      variant="floating"
      message={config.message}
      title={config.title}
      severity={config.severity || 'info'}
      showIcon={config.showIcon}
      icon={config.icon}
      buttonText={config.buttonText}
      storageKey={storageKey}
      storageStrategy={storageStrategy}
      dismissalDuration={dismissalDuration}
      onDismiss={handleCloseClick}
      onButtonClick={config.buttonText ? handleButtonClick : undefined}
      testId={testId}
    />
  );
};

export default PageBanner; 